// src/auth/AuthProvider.tsx
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import type { User } from "@supabase/supabase-js";
import { apiFetch } from "../lib/api";

type Plan = "free" | "premium";

type AuthCtx = {
  user: User | null;
  accessToken: string | null;
  plan: Plan;
  loading: boolean;
 
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>; 
  signInWithGoogle: () => Promise<void>;
  signInWithGitHub: () => Promise<void>;
  signOut: () => Promise<void>;
  refreshPlan: () => Promise<void>;
}; 

const Ctx = createContext<AuthCtx>({
  user: null,
  accessToken: null,
  plan: "free",
  loading: true,
  signIn: async () => {},
  signUp: async () => {},
  sendPasswordReset: async () => {},
  signInWithGoogle: async () => {},
  signInWithGitHub: async () => {},
  signOut: async () => {},
  refreshPlan: async () => {},
}); 

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [plan, setPlan] = useState<Plan>("free");
  const [loading, setLoading] = useState(true);

  const fetchPlan = async (u: User | null) => {
    if (!u) {
      setPlan("free");
      return;
    }

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;

      if (!token) {
        setPlan("free");
        return;
      }

      const res = await apiFetch("/api/me/subscription");

      if (!res.ok) {
        console.error("[fetchPlan] subscription endpoint error:", res.status);
        setPlan("free");
        return;
      }

      const data = await res.json();

      console.log("[fetchPlan] subscription response:", data);

      const untilOk =
        !data?.premium_until || new Date(data.premium_until) > new Date();

      const premium = !!data?.is_premium && untilOk;

      setPlan(premium ? "premium" : "free");
    } catch (err) {
      console.error("[fetchPlan] subscription read error:", err);
      setPlan("free");
    }
  };

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(async ({ data }) => {
      if (!mounted) return;

      const session = data.session;
      const u = session?.user ?? null;

      setUser(u);
      setAccessToken(session?.access_token ?? null);
      await fetchPlan(u);
      setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_evt, session) => {
      const u = session?.user ?? null;

      setUser(u);
      setAccessToken(session?.access_token ?? null);

      // Run after Supabase finishes processing the auth event.
      window.setTimeout(() => {
        void fetchPlan(u);
      }, 0);
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const value = useMemo<AuthCtx>(
    () => ({
      user,
      accessToken,
      plan,
      loading,

      async signIn(email: string, password: string) {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      },

      async signUp(email: string, password: string) {
        const { error } = await supabase.auth.signUp({ email, password });
        if (error) throw error;
      },

      async sendPasswordReset(email: string) {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: window.location.origin,
        });
        if (error) throw error;
      },

      async signInWithGoogle() {
        const { error } = await supabase.auth.signInWithOAuth({
          provider: "google",
          options: {
            redirectTo: window.location.origin,
          },
        });

        if (error) throw error;
      },

      async signInWithGitHub() {
        const { error } = await supabase.auth.signInWithOAuth({
          provider: "github",
          options: {
            redirectTo: window.location.origin,
          },
        });

        if (error) throw error;
      },

      async signOut() {
        setUser(null);
        setAccessToken(null);
        setPlan("free");

        try {
          await supabase.auth.signOut({ scope: "local" });
        } catch {
          Object.keys(localStorage).forEach((k) => {
            if (k.startsWith("sb-")) localStorage.removeItem(k);
          });
          Object.keys(sessionStorage).forEach((k) => {
            if (k.startsWith("sb-")) sessionStorage.removeItem(k);
          });
        }
      },

      async refreshPlan() {
        await fetchPlan(user);
      },
    }),
    [user, accessToken, plan, loading]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  return useContext(Ctx);
} 